import { useCallback, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { Button, Dropdown, Menu, message, Modal, Select } from 'antd';
import * as Icons from '@ant-design/icons';
import { StopPropagation } from '@core/ui';
import { OverCell, tableColumns } from '@core/table';
import { useService } from '@core/inversify-react';
import { useBus } from '@core/event-bus';

import { IBox, boxQueryKeys, IRemoveBoxesRepo, RemoveBoxesRepoType } from '../../';
import { useBranches } from '@modules/branches';
import { filterOption } from '@core/form';
import { BoxTransfersRequestType } from '@modules/box-transfers';

export const useBoxesTableColumns = () => {
  const history = useHistory();
  const location = useLocation();
  const branches = useBranches();

  const { publish } = useBus();

  const removeBoxesRepo = useService<IRemoveBoxesRepo>(RemoveBoxesRepoType);

  const actionsColumn = useMemo<Column<IBox>>(
    () => ({
      ...tableColumns.actions,
      Cell: ({ row: { original } }) => {
        const [visible, setVisible] = useState<boolean>(false);

        const openEdit = useCallback(() => {
          history.push(generatePath('/@next/boxes/:id/update', { id: original.id }), { background: location });
        }, [original.id]);

        const remove = useCallback(() => {
          const onOk = async () => {
            const result = await removeBoxesRepo.execute([original.id]);

            if (result.status === 200) {
              publish({ type: '@boxes/remove/succeed', payload: [original.id] });
            } else {
              message.error(result.response);
            }
          };

          Modal.confirm({ title: 'Diqqət', content: 'Sifarişi silməyə əminsinizmi?', onOk });
        }, [original.id]);

        const openBoxHistory = useCallback(() => {
          history.push(generatePath('/@next/box-transfers/:id/:type', { id: original.id, type: BoxTransfersRequestType.CONTAINER }));
        }, [original.id]);

        const overlay = (
          <Menu onClick={() => setVisible(false)}>
            <Menu.Item onClick={openBoxHistory} icon={<Icons.HistoryOutlined />}>
              Tarixçə
            </Menu.Item>
            <Menu.Item onClick={openEdit} icon={<Icons.EditOutlined />}>
              Düzəliş et
            </Menu.Item>
            <Menu.Item onClick={remove} icon={<Icons.DeleteOutlined />}>
              Sil
            </Menu.Item>
          </Menu>
        );

        return (
          <StopPropagation>
            <Dropdown visible={visible} onVisibleChange={setVisible} overlay={overlay}>
              <Button icon={<Icons.MoreOutlined />} size='small' />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [history, location, publish, removeBoxesRepo],
  );

  const baseColumns = useMemo<Column<IBox>[]>(
    () => [
      {
        ...tableColumns.small,
        accessor: (row) => row.id,
        id: boxQueryKeys.id,
        Header: 'Kod',
      },
      {
        accessor: (row) => row.name,
        id: boxQueryKeys.name,
        Header: 'Ad',
      },
      {
        accessor: (row) => row.branch?.name,
        id: boxQueryKeys.branch.id,
        Filter: ({ column: { filterValue, setFilter } }) => {
          return (
            <Select showSearch={true} filterOption={filterOption} allowClear={true} style={{ width: '100%' }} onChange={setFilter} value={filterValue}>
              {branches.data?.map((branches) => (
                <Select.Option key={branches.id} value={branches.id.toString()}>
                  {branches.name}
                </Select.Option>
              ))}
            </Select>
          );
        },
        Header: 'Filial',
        Cell: OverCell,
      },
      {
        ...tableColumns.normal,
        accessor: (row) => row.declarationCount,
        id: boxQueryKeys.declarationCount,
        Cell: ({ cell: { value } }) => `${value} ədəd`,
        Header: 'Bağlama sayı',
        filterable: false,
      },
      {
        ...tableColumns.date,
        accessor: (row) => row.createdAt,
        id: boxQueryKeys.createdAt,
        Header: 'Yaradılma tarixi',
      },
    ],
    [branches.data],
  );

  return useMemo(() => {
    return [actionsColumn, ...baseColumns];
  }, [actionsColumn, baseColumns]);
};
